import { useEffect, useState } from "react";
import { CalendarDays, Check, Loader2, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

/**
 * "Create a follow-up task?" prompt, shown right after a task is completed —
 * leads often need several touches, so the next one is a click away. Title is
 * prefilled, due date comes from quick presets and the optional note becomes
 * the new task's description.
 *
 * Opens immediately on click, while the completion request may still be in
 * flight (`saving`): the header shows the save progress and the form is
 * usable right away; Create unlocks once the completion is saved.
 */

const DUE_PRESETS: { label: string; days: number }[] = [
  { label: "Tomorrow", days: 1 },
  { label: "In 3 days", days: 3 },
  { label: "In 1 week", days: 7 },
  { label: "In 2 weeks", days: 14 },
];

/** N days from today at 8:00 AM local time, as ISO. */
const dueAtFor = (days: number): string => {
  const due = new Date();
  due.setDate(due.getDate() + days);
  due.setHours(8, 0, 0, 0);
  return due.toISOString();
};

const inputClass =
  "w-full rounded-lg border border-white/[0.08] bg-white/[0.03] px-2.5 py-1.5 text-[13px] text-white placeholder:text-slate-500 transition focus:border-brand-400/70 focus:bg-white/[0.05] focus:outline-none focus:ring-2 focus:ring-brand-400/20";

export type TFollowUpValues = { title: string; dueAt: string; note: string };

type Props = {
  open: boolean;
  leadName: string;
  /** Completion request still in flight — header shows progress, Create waits. */
  saving?: boolean;
  onClose: () => void;
  onCreate: (values: TFollowUpValues) => Promise<void>;
};

export default function FollowUpTaskPrompt({ open, leadName, saving = false, onClose, onCreate }: Props) {
  const [title, setTitle] = useState("");
  const [days, setDays] = useState(DUE_PRESETS[0].days);
  const [note, setNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fresh form each time the prompt opens for a newly completed task.
  useEffect(() => {
    if (!open) return;
    setTitle(`Follow up with ${leadName}`);
    setDays(DUE_PRESETS[0].days);
    setNote("");
    setIsSubmitting(false);
  }, [open, leadName]);

  const dueDateLabel = new Date(dueAtFor(days)).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

  const canSubmit = title.trim() !== "" && !isSubmitting && !saving;

  const handleCreate = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      await onCreate({ title: title.trim(), dueAt: dueAtFor(days), note: note.trim() });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !isSubmitting && onClose()}>
      <DialogContent className="z-[200] w-full max-w-[90vw] gap-0 overflow-hidden rounded-2xl border border-white/[0.08] bg-gradient-to-b from-ink-800/95 to-ink-900/95 p-0 shadow-[0_32px_90px_-16px_rgba(0,0,0,0.75)] backdrop-blur-xl sm:max-w-[400px] [&>button:last-child]:right-3.5 [&>button:last-child]:top-3.5 [&>button:last-child]:text-slate-400">
        {/* Top accent — brand while saving, green once the completion landed */}
        <div
          className={cn(
            "h-[2px] w-full bg-gradient-to-r from-transparent to-transparent transition-colors duration-500",
            saving ? "via-brand-400/70" : "via-accent-green/70"
          )}
        />

        <div className="p-5">
          {/* Header */}
          <div className="flex items-center gap-3 pr-6">
            <span
              className={cn(
                "relative grid h-10 w-10 shrink-0 place-items-center rounded-full border transition-colors duration-500",
                saving
                  ? "border-brand-400/30 bg-brand-500/10 text-brand-300"
                  : "border-accent-green/30 bg-accent-green/10 text-accent-green"
              )}
            >
              {saving ? (
                <Loader2 className="h-[18px] w-[18px] animate-spin" />
              ) : (
                <>
                  <span className="absolute inset-0 rounded-full bg-accent-green/15 animate-ping-soft" style={{ animationIterationCount: 2 }} />
                  <Check className="h-[18px] w-[18px]" strokeWidth={2.5} />
                </>
              )}
            </span>
            <div className="min-w-0">
              <DialogTitle className="text-[14px] font-semibold text-white">
                {saving ? "Completing task…" : "Task completed"}
              </DialogTitle>
              <p className="mt-0.5 truncate text-[12px] text-slate-400">
                {saving ? "Saving — you can set up the next step meanwhile." : <>Keep the momentum — follow up with <span className="font-medium text-slate-200">{leadName}</span>?</>}
              </p>
            </div>
          </div>

          <form
            className="mt-4 space-y-3.5"
            onSubmit={(e) => {
              e.preventDefault();
              handleCreate();
            }}
          >
            <label className="block">
              <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">Task</span>
              <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} />
            </label>

            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">Due</span>
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-400">
                  <CalendarDays className="h-3 w-3 text-slate-500" />
                  {dueDateLabel}
                </span>
              </div>
              <div className="grid grid-cols-4 gap-1.5">
                {DUE_PRESETS.map((p) => (
                  <button
                    key={p.days}
                    type="button"
                    onClick={() => setDays(p.days)}
                    className={cn(
                      "rounded-lg border px-1 py-1.5 text-[11.5px] font-medium transition",
                      days === p.days
                        ? "border-brand-400/50 bg-brand-500/20 text-brand-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
                        : "border-white/[0.06] bg-white/[0.03] text-slate-400 hover:border-white/[0.12] hover:bg-white/[0.06] hover:text-slate-200"
                    )}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <label className="block">
              <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                Note <span className="font-medium normal-case tracking-normal text-slate-600">(optional)</span>
              </span>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="e.g. Credit score 620→630, on pace — check in next month."
                className={cn(inputClass, "resize-none text-[12.5px]")}
              />
            </label>

            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="rounded-lg px-3 py-1.5 text-[12px] font-medium text-slate-400 transition hover:bg-white/[0.06] hover:text-white disabled:opacity-50"
              >
                Not now
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-br from-brand-400 to-brand-600 px-3.5 py-1.5 text-[12px] font-semibold text-white shadow-[0_8px_20px_-6px_rgba(59,130,246,0.55)] transition hover:translate-y-[-1px] disabled:pointer-events-none disabled:opacity-50"
              >
                {isSubmitting || saving ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Plus className="h-3.5 w-3.5" />
                )}
                Create task
              </button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
